import { CERCO, PALETTE } from "@eldrun/content";
import { BufferAttribute, BufferGeometry, Mesh, type Object3D, type ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { createCercoMaterial } from "../render/shaders/cerco.ts";
import { ArenaRings } from "./arena-rings.ts";

export interface CercoLike {
  active: boolean;
  x: number;
  z: number;
  radius: number;
  nextX: number;
  nextZ: number;
  nextRadius: number;
  closesIn: number;
  closing: boolean;
  damage: number;
}

/** Vinheta de fogo: começa 10 m antes da borda e fica cheia fora do anel (0 a 1). */
export function cercoVignette(distance: number, radius: number): number {
  return Math.max(0, Math.min(1, (distance - (radius - CERCO.tossMargin)) / CERCO.tossMargin));
}

/** Linha do HUD do Cerco: fora dele (o dano), fechando, ou quando fecha. */
export function cercoText(c: CercoLike, outside: boolean): string | undefined {
  if (!c.active) return undefined;
  if (outside) return `Fora do Cerco! −${c.damage} % da Vida por segundo`;
  if (c.closing) return "O Cerco está fechando";
  if (c.closesIn > 0) return `O Cerco fecha em ${c.closesIn} s`;
  return undefined;
}

/** A parede sai de um pouco abaixo do chão (ou do mar) e sobe 60 m: segue o relevo da borda. */
const WALL_HEIGHT = 60;
const WALL_SINK = 8;
const WALL_SEGMENTS = 160;

/** Cilindro aberto em coordenadas de mundo, base no chão de cada ponto da borda (uv.y de 0 a 1). */
function wallGeometry(): BufferGeometry {
  const g = new BufferGeometry();
  const n = WALL_SEGMENTS + 1;
  const uv = new Float32Array(n * 4);
  const index: number[] = [];
  for (let i = 0; i < n; i++) {
    uv.set([i / WALL_SEGMENTS, 0, i / WALL_SEGMENTS, 1], i * 4);
    if (i < WALL_SEGMENTS) {
      const a = i * 2;
      index.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
    }
  }
  g.setAttribute("position", new BufferAttribute(new Float32Array(n * 6), 3));
  g.setAttribute("normal", new BufferAttribute(new Float32Array(n * 6), 3));
  g.setAttribute("uv", new BufferAttribute(uv, 2));
  g.setIndex(index);
  return g;
}

/** Cerco do Boitatá no cliente: a parede de fogo (shader `cerco`) e a linha do próximo anel. */
export class CercoLayer {
  private readonly mesh: Mesh;
  private readonly material: ShaderMaterial;
  private readonly next: ArenaRings;
  private nextKey = "";
  private wallKey = "";
  /** Chão de cada ponto (o maior entre o terreno e o mar). */
  private readonly ground: (x: number, z: number) => number;

  constructor(root: Object3D, atmosphere: AtmosphereUniforms, ground: (x: number, z: number) => number) {
    this.ground = ground;
    this.material = createCercoMaterial(atmosphere);
    this.mesh = new Mesh(wallGeometry(), this.material);
    // Vértices já em coordenadas de mundo, refeitos quando o anel anda.
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 7;
    this.mesh.visible = false;
    root.add(this.mesh);
    this.next = new ArenaRings(root, ground, PALETTE.neutro.hex);
  }

  update(c: CercoLike, playerX: number, playerZ: number, time: number): void {
    this.mesh.visible = c.active && c.radius > 0.5;
    if (!this.mesh.visible) {
      this.next.sync([]);
      return;
    }
    this.shapeWall(c.x, c.z, c.radius);
    const u = this.material.uniforms;
    (u["uCentro"]!.value as { set(x: number, y: number): void }).set(c.x, c.z);
    u["uRaio"]!.value = c.radius;
    u["uRaioProximo"]!.value = c.nextRadius;
    u["uDistJogador"]!.value = c.radius - Math.hypot(playerX - c.x, playerZ - c.z);
    u["uTempo"]!.value = time;
    // O próximo círculo, anunciado ao amanhecer, desenhado no chão até o anel chegar nele.
    const showNext = c.nextRadius < c.radius - 0.5;
    const key = showNext ? `${c.nextX.toFixed(1)},${c.nextZ.toFixed(1)},${c.nextRadius.toFixed(1)}` : "";
    if (key !== this.nextKey) {
      this.nextKey = key;
      this.next.sync([]);
      if (showNext) this.next.sync([[key, { x: c.nextX, z: c.nextZ, radius: c.nextRadius }]]);
    }
  }

  /** Base de cada coluna no chão daquele ponto da borda: a parede não some atrás de morros. */
  private shapeWall(x: number, z: number, radius: number): void {
    const key = `${x.toFixed(2)},${z.toFixed(2)},${radius.toFixed(2)}`;
    if (key === this.wallKey) return;
    this.wallKey = key;
    const position = this.mesh.geometry.getAttribute("position") as BufferAttribute;
    const normal = this.mesh.geometry.getAttribute("normal") as BufferAttribute;
    for (let i = 0; i <= WALL_SEGMENTS; i++) {
      const a = (i / WALL_SEGMENTS) * Math.PI * 2;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const px = x + cos * radius;
      const pz = z + sin * radius;
      const base = this.ground(px, pz) - WALL_SINK;
      position.setXYZ(i * 2, px, base, pz);
      position.setXYZ(i * 2 + 1, px, base + WALL_HEIGHT, pz);
      normal.setXYZ(i * 2, cos, 0, sin);
      normal.setXYZ(i * 2 + 1, cos, 0, sin);
    }
    position.needsUpdate = true;
    normal.needsUpdate = true;
  }

  /** Altura da base e do topo da parede no ângulo 0 (testes). */
  wallSpan(): { base: number; top: number } {
    const position = this.mesh.geometry.getAttribute("position") as BufferAttribute;
    return { base: position.getY(0), top: position.getY(1) };
  }

  get visible(): boolean {
    return this.mesh.visible;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.next.dispose();
  }
}
